import React, { useState, useEffect, useCallback } from 'react';
import { Card, Table, Tag, Typography, Space, Button, Modal } from 'antd';
import { EyeOutlined, CheckSquareOutlined, TrophyOutlined } from '@ant-design/icons';
import { useNavigate } from 'react-router-dom';
import dayjs from 'dayjs';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import { setMessage } from '../../../redux/message.reducer';
import '../../order/orders/Orders.css';
import './MyCompletions.css';

const { Title } = Typography;
const DEFAULT_PAGE_SIZE = 10;

function openAttachmentUrl(url) {
  if (!url || typeof url !== 'string') return;
  if (url.startsWith('data:')) {
    try {
      fetch(url)
        .then((res) => res.blob())
        .then((blob) => {
          const blobUrl = URL.createObjectURL(blob);
          window.open(blobUrl, '_blank', 'noopener,noreferrer');
          setTimeout(() => URL.revokeObjectURL(blobUrl), 60000);
        })
        .catch(() => window.open(url, '_blank', 'noopener,noreferrer'));
    } catch {
      window.open(url, '_blank', 'noopener,noreferrer');
    }
  } else {
    window.open(url, '_blank', 'noopener,noreferrer');
  }
}

const STATUS_MAP = {
  pending: { color: 'orange', label: 'Pending' },
  verified: { color: 'green', label: 'Verified' },
  rejected: { color: 'red', label: 'Rejected' },
};

export default function MyCompletions({ embedded = false }) {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [pagination, setPagination] = useState({ current: 1, pageSize: DEFAULT_PAGE_SIZE, total: 0 });
  const [viewRecord, setViewRecord] = useState(null);

  const fetchCompletions = useCallback(async (page, limit) => {
    setLoading(true);
    try {
      const res = await axios.get('/task-completions/my', { params: { page, limit } });
      setData(res.data?.data ?? []);
      setPagination((prev) => ({
        ...prev,
        current: res.data?.page ?? page,
        pageSize: res.data?.limit ?? limit,
        total: res.data?.total ?? 0,
      }));
    } catch (err) {
      dispatch(setMessage({ message: err.response?.data?.error || 'Failed to load', type: 'error' }));
      setData([]);
    } finally {
      setLoading(false);
    }
  }, [dispatch]);

  useEffect(() => {
    fetchCompletions(pagination.current, pagination.pageSize);
  }, [pagination.current, pagination.pageSize, fetchCompletions]);

  const columns = [
    {
      title: 'Task',
      key: 'task',
      render: (_, record) => record.taskId?.title ?? '—',
    },
    {
      title: 'Type',
      key: 'taskType',
      render: (_, record) => record.taskId?.taskType === 'recurring' ? 'Recurring' : 'One Time',
    },
    {
      title: 'Status',
      dataIndex: 'status',
      key: 'status',
      width: 110,
      render: (status) => {
        const s = STATUS_MAP[status] || { color: 'default', label: status };
        return <Tag color={s.color}>{s.label}</Tag>;
      },
    },
    {
      title: 'Reward pts',
      dataIndex: 'rewardPoints',
      key: 'rewardPoints',
      width: 120,
      render: (val) => (val != null ? <Tag color="gold">{val}</Tag> : '—'),
    },
    {
      title: 'Submitted',
      dataIndex: 'submittedAt',
      key: 'submittedAt',
      width: 150,
      render: (val) => (val ? dayjs(val).format('MMM D, YYYY h:mm A') : '—'),
    },
    {
      title: 'Actions',
      key: 'actions',
      width: 80,
      fixed: 'right',
      render: (_, record) => (
        <Button type="text" size="small" icon={<EyeOutlined />} onClick={() => setViewRecord(record)}>
          View
        </Button>
      ),
    },
  ];

  return (
    <div className="my-completions-page">
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        {!embedded && (
          <div className="orders-header">
            <div className="orders-header-title-row">
              <Title level={3} style={{ margin: 0 }}>
                My completions
              </Title>
              <div className="orders-header-actions">
                <Button
                  type="primary"
                  icon={<CheckSquareOutlined />}
                  onClick={() => navigate('/my-tasks')}
                  className="orders-add-btn app-btn-primary"
                >
                  My tasks
                </Button>
                <Button
                  type="primary"
                  icon={<TrophyOutlined />}
                  onClick={() => navigate('/my-rewards')}
                  className="orders-add-btn app-btn-primary"
                >
                  My rewards
                </Button>
              </div>
            </div>
          </div>
        )}
        <Card className="my-completions-card">
          <Table
            columns={columns}
            dataSource={data.map((r) => ({ ...r, key: r._id }))}
            loading={loading}
            pagination={{
              current: pagination.current,
              pageSize: pagination.pageSize,
              total: pagination.total,
              showSizeChanger: true,
              showTotal: (t) => `Total ${t} completions`,
              pageSizeOptions: ['10', '20', '50'],
            }}
            onChange={(pag) =>
              setPagination((prev) => ({
                ...prev,
                current: pag.current ?? prev.current,
                pageSize: pag.pageSize ?? prev.pageSize,
              }))
            }
            size="middle"
            responsive
            scroll={{ x: 700 }}
          />
        </Card>
      </Space>

      <Modal
        title={viewRecord?.taskId?.title ? `Completion: ${viewRecord.taskId.title}` : 'Completion details'}
        open={!!viewRecord}
        onCancel={() => setViewRecord(null)}
        footer={[<Button key="close" onClick={() => setViewRecord(null)}>Close</Button>]}
        width={520}
        destroyOnClose
      >
        {viewRecord && (
          <div className="my-completions-view">
            <div className="my-completions-view-row">
              <span className="my-completions-view-label">Status</span>
              <Tag color={(STATUS_MAP[viewRecord.status] || {}).color}>
                {(STATUS_MAP[viewRecord.status] || {}).label ?? viewRecord.status}
              </Tag>
            </div>
            {viewRecord.rewardPoints != null && (
              <div className="my-completions-view-row">
                <span className="my-completions-view-label">Reward points</span>
                <span>{viewRecord.rewardPoints}</span>
              </div>
            )}
            <div className="my-completions-view-row">
              <span className="my-completions-view-label">Submitted</span>
              <span>{viewRecord.submittedAt ? dayjs(viewRecord.submittedAt).format('MMM D, YYYY h:mm A') : '—'}</span>
            </div>
            {viewRecord.description && (
              <div className="my-completions-view-section">
                <div className="my-completions-view-section-title">Description</div>
                <div style={{ whiteSpace: 'pre-wrap' }}>{viewRecord.description}</div>
              </div>
            )}
            {Array.isArray(viewRecord.workResult) && viewRecord.workResult.length > 0 && (
              <div className="my-completions-view-section">
                <div className="my-completions-view-section-title">Work result</div>
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  {viewRecord.workResult.map((f, i) => (
                    <li key={i}>
                      <a
                        href="#"
                        onClick={(e) => { e.preventDefault(); openAttachmentUrl(f.url); }}
                      >
                        {f.name}
                      </a>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Modal>
    </div>
  );
}
